<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { CreditCard, KeyRound, Palette, Sparkles, User, Users } from 'lucide-vue-next';
import Heading from '@/components/Heading.vue';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useCurrentUrl } from '@/composables/useCurrentUrl';
import { toUrl } from '@/lib/utils';
import { edit as editAppearance } from '@/routes/appearance';
import { index as billingIndex } from '@/routes/billing';
import { edit as editBrand } from '@/routes/brand';
import { edit as editProfile } from '@/routes/profile';
import { index as teams } from '@/routes/teams';
import { edit as editPassword } from '@/routes/user-password';
import type { NavItem } from '@/types';

const sidebarNavItems: NavItem[] = [
    { title: 'Profile', href: editProfile(), icon: User },
    { title: 'Password', href: editPassword(), icon: KeyRound },
    { title: 'Brand Identity', href: editBrand(), icon: Sparkles },
    { title: 'Teams', href: teams(), icon: Users },
    { title: 'Appearance', href: editAppearance(), icon: Palette },
    { title: 'Billing', href: billingIndex(), icon: CreditCard },
];

const { isCurrentOrParentUrl } = useCurrentUrl();
</script>

<template>
    <div class="ct-page">
        <Heading
            title="Settings"
            description="Manage your profile, brand, team, and subscription."
        />

        <div class="flex flex-col lg:flex-row lg:space-x-12">
            <aside class="w-full max-w-xl lg:w-56">
                <nav class="flex flex-col space-y-1" aria-label="Settings">
                    <Button
                        v-for="item in sidebarNavItems"
                        :key="toUrl(item.href)"
                        variant="ghost"
                        :class="[
                            'w-full justify-start gap-2',
                            { 'bg-muted': isCurrentOrParentUrl(item.href) },
                        ]"
                        as-child
                    >
                        <Link :href="item.href">
                            <component :is="item.icon" class="h-4 w-4" />
                            {{ item.title }}
                        </Link>
                    </Button>
                </nav>
            </aside>

            <Separator class="my-6 lg:hidden" />

            <div class="flex-1 md:max-w-3xl">
                <section class="max-w-2xl space-y-12">
                    <slot />
                </section>
            </div>
        </div>
    </div>
</template>
